import { useContext, useEffect, useMemo, useState } from "react";

import AppContext from "../../../context/AppContext";
import {
  DATE_RANGE,
  KPI_CARD_BACKGROUND,
  SIDER_TABS,
} from "../../../utils/Constants";
import { AppStrings } from "../../../utils/constants/AppStrings";
import {
  Button,
  DataTable,
  DatePicker,
  KpiCard,
  Select,
} from "../../shared";
import { useGetAcquisitionKpis } from "../../../queries/promotions-acquisition/useGetAcquisitionKpis";
import { useGetAcquisitionSources } from "../../../queries/promotions-acquisition/useGetAcquisitionSources";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { formatCurrency as formatCurrencyShared } from "../../../utils/financialFormatters";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import styles from "./UtmReports.module.css";
import { getAdminNow } from "../../../utils/dateService";

const formatInteger = (value) =>
  Number(value ?? 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

// Both UTM filters are multi-value because they're applied client-side
// off the dataset the BE returned (the v1 endpoint doesn't accept them
// as query params). The BE-supported `state` filter was removed
// intentionally — it's documented but doesn't work in production today,
// so exposing it would mislead operators.
const EMPTY_FILTERS = Object.freeze({
  utm_categorization: [],
  utm_source: [],
});

// Matches the BE default page size so the first request lines up with
// the contract's `data.size: 50` example. Using 50 here keeps the
// page-size selector's initial selection consistent with what the BE
// returned on the first paint.
const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Adding a card → append here AND in `ACQUISITION_KPI_KEYS`.
// KpiCard hides the symbol when maximumFractionDigits === 0, so currency KPIs use 2.
const KPI_DEFINITIONS = [
  {
    key: "total_users",
    get title() {
      return AppStrings.PAM_UTM_TOTAL_USERS;
    },
    maximumFractionDigits: 0,
  },
  {
    key: "total_ftds",
    get title() {
      return AppStrings.PAM_UTM_TOTAL_FTDS;
    },
    maximumFractionDigits: 0,
  },
  {
    key: "avg_net_revenue",
    get title() {
      return AppStrings.PAM_UTM_AVG_NET_REVENUE;
    },
    maximumFractionDigits: 2,
  },
  {
    key: "deposit_amount",
    get title() {
      return AppStrings.PAM_UTM_DEPOSIT_AMOUNT;
    },
    maximumFractionDigits: 2,
  },
  {
    key: "withdrawal_amount",
    get title() {
      return AppStrings.PAM_UTM_WITHDRAWAL_AMOUNT;
    },
    maximumFractionDigits: 2,
  },
  {
    key: "revenue",
    get title() {
      return AppStrings.PAM_UTM_REVENUE;
    },
    maximumFractionDigits: 2,
  },
];

// Single source of truth for how a raw row value collapses into a filter
// `value`. Both `toOption` and the predicate in `tableRows.matches` go
// through this helper so picking "Paid Media" in the dropdown actually
// matches a row whose `utm_source` is "paid media".
const normaliseFilterValue = (raw) =>
  (raw ?? "").trim().toLowerCase().replace(/\s+/g, "_");

const toOption = (raw) => {
  const label = (raw ?? "").trim();
  if (!label) return null;
  return { value: normaliseFilterValue(label), label };
};

const deriveOptions = (rows, field) => {
  const seen = new Map();
  for (const row of rows) {
    const option = toOption(row?.[field]);
    if (option && !seen.has(option.value)) seen.set(option.value, option);
  }
  return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
};

/**
 * Promotions → UTM Reports.
 *
 * Gated in `App.js` by
 * `protectedElement(<UtmReports />, { requiredPermission: "promotions.utm_reports" })`
 * and in `Sider` by the matching dev-only entry in `PROMOTIONS_OPTIONS`,
 * so it is only reachable in dev/test envs until the backend permission
 * ships.
 *
 * The date range drives both BE queries. UTM Categorization and UTM
 * Source filters narrow the table client-side off the same response, so
 * the dropdown options are derived from the rows the BE returned. The
 * BE-supported State filter is intentionally not surfaced — it doesn't
 * work in v1 production today.
 */
const UtmReports = () => {
  const { setActiveSidebarTab } = useContext(AppContext);
  usePageTitle(AppStrings.UTM_REPORTS);

  const currencySymbol = useCurrencySymbol() ?? "$";
  const formatCurrency = (value, { fractionDigits = 2 } = {}) =>
    formatCurrencyShared(Number(value ?? 0), currencySymbol, fractionDigits);

  const [dates, setDates] = useState([
    getAdminNow().startOf("month"),
    getAdminNow().endOf("day"),
  ]);

  // `draftFilters` tracks the dropdowns as the user changes them;
  // `appliedFilters` is what the BE queries + table actually read from.
  // The split is what the orange Apply button gates on — same pattern as
  // Affiliates, but explicit because UTM Reports also surfaces an Apply CTA.
  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  // Server-driven pagination: `currentPage` is 1-indexed (BE contract).
  // Reset to page 1 whenever filters change so the user doesn't end up on
  // a no-longer-existing page (e.g. narrowing to a UTM Source whose slice
  // has fewer pages than the one the user is currently looking at).
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.PROMOTIONS, "UTM_REPORTS");
  }, [setActiveSidebarTab]);

  const handleApply = () => {
    setAppliedFilters(draftFilters);
    setCurrentPage(1);
  };

  // Reset both `draftFilters` (so the dropdowns blank out) and
  // `appliedFilters` (so the table re-shows the full slice) in one go —
  // resetting only the draft would leave the table filtered until the
  // user pressed Apply, which isn't what "Clear filters" implies. Page
  // also resets to 1 so we don't land on a stale page from the filtered
  // slice. The date range is intentionally left alone — it lives in the
  // header (a separate primary control), not in the filter row.
  const handleClearFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  // Wrap setDates so the date range and page reset stay in lockstep —
  // changing the window resets pagination to avoid querying e.g. page 5
  // of a slice that may now only have 1 page of results.
  const handleDatesChange = (nextDates) => {
    setDates(nextDates);
    setCurrentPage(1);
  };

  // The KPI hook only needs the date range; pagination is irrelevant to
  // the totals it returns. Sources gets the same plus `page`/`size` so
  // the BE-driven pagination updates when the user navigates the table.
  const kpiQueryFilters = useMemo(
    () => ({
      start_date: dates?.[0]?.format("YYYY-MM-DD") ?? "",
      end_date: dates?.[1]?.format("YYYY-MM-DD") ?? "",
    }),
    [dates],
  );

  const sourcesQueryFilters = useMemo(
    () => ({
      ...kpiQueryFilters,
      page: currentPage,
      size: pageSize,
    }),
    [kpiQueryFilters, currentPage, pageSize],
  );

  const {
    data: kpiCards = [],
    isFetching: isKpisLoading,
    isError: isKpisError,
  } = useGetAcquisitionKpis(kpiQueryFilters);

  const {
    data: sourcesPayload,
    isFetching: isSourcesLoading,
    isError: isSourcesError,
  } = useGetAcquisitionSources(sourcesQueryFilters);

  // Memoised so downstream useMemo hooks don't pick up a fresh array
  // identity on every render (the `?? []` fallback would otherwise create
  // a new empty array each time and invalidate their caches).
  const sourceRows = useMemo(
    () => sourcesPayload?.rows ?? [],
    [sourcesPayload?.rows],
  );

  // KPI cards arrive keyed by `key` (e.g. "deposit_amount"); index them
  // once so the render below is a simple O(1) lookup per card.
  const kpiByKey = useMemo(() => {
    const map = new Map();
    for (const card of kpiCards) map.set(card.key, card);
    return map;
  }, [kpiCards]);

  // UTM Categorization and UTM Source dropdowns are derived from the
  // dataset itself so the user can only pick values that actually appear
  // in the current slice — utm_source is documented as a free-form
  // lowercased string and categorization is a small BE-enforced enum,
  // both safe to read off the rows we just rendered.
  const utmCategorizationOptions = useMemo(
    () => deriveOptions(sourceRows, "utmCategorization"),
    [sourceRows],
  );
  const utmSourceOptions = useMemo(
    () => deriveOptions(sourceRows, "utmSource"),
    [sourceRows],
  );

  // Drives the visibility of the Clear Filters button — it stays hidden
  // until at least one filter is actually in effect on the table, so the
  // header doesn't carry a no-op action on first paint. Keyed off
  // `appliedFilters` (not `draftFilters`) on purpose: an unapplied draft
  // selection isn't filtering anything yet, so there's nothing to clear.
  const hasAppliedFilters =
    (appliedFilters.utm_categorization?.length ?? 0) > 0 ||
    (appliedFilters.utm_source?.length ?? 0) > 0;

  const tableRows = useMemo(() => {
    const matches = (row) => {
      if (
        appliedFilters.utm_categorization?.length &&
        !appliedFilters.utm_categorization.includes(
          normaliseFilterValue(row.utmCategorization),
        )
      ) {
        return false;
      }
      if (
        appliedFilters.utm_source?.length &&
        !appliedFilters.utm_source.includes(
          normaliseFilterValue(row.utmSource),
        )
      ) {
        return false;
      }
      return true;
    };

    return sourceRows.filter(matches);
  }, [sourceRows, appliedFilters]);

  // Pagination semantics depend on whether a client-side filter is in
  // effect. Without a filter, `total` is the server's full row count so
  // the user can walk through every BE page. With a filter applied, the
  // table is showing a subset of the *current* BE page only — using the
  // server total would falsely advertise more rows than the user can
  // reach (clicking Next would jump to a different unfiltered BE page
  // and re-apply the filter there, which is confusing). Collapsing to
  // `tableRows.length` makes the pager match what's actually on screen
  // and effectively disables page navigation while the filter is on.
  const totalRows = hasAppliedFilters
    ? tableRows.length
    : sourcesPayload?.pagination?.total ?? sourceRows.length;

  // UTM Categorization / UTM Source render as free-form labels — sorting
  // them alphabetically wasn't meaningful for stakeholders, so the sort
  // affordance is intentionally absent on those two columns. The numeric
  // columns below keep their sorters because ranking by FTDs / dollar
  // amounts is genuinely useful when reading the table.
  const columns = useMemo(
    () => [
      {
        title: AppStrings.PAM_UTM_CATEGORIZATION,
        dataIndex: "utmCategorization",
        key: "utmCategorization",
        width: 180,
      },
      {
        title: AppStrings.UTM_SOURCE,
        dataIndex: "utmSource",
        key: "utmSource",
        width: 160,
      },
      {
        title: AppStrings.PAM_UTM_TOTAL_FTDS,
        dataIndex: "totalFtds",
        key: "totalFtds",
        width: 140,
        sorter: (a, b) => (a.totalFtds ?? 0) - (b.totalFtds ?? 0),
        render: (value) => formatInteger(value),
      },
      {
        title: AppStrings.PAM_UTM_DEPOSIT_AMOUNT,
        dataIndex: "depositAmount",
        key: "depositAmount",
        width: 160,
        sorter: (a, b) => (a.depositAmount ?? 0) - (b.depositAmount ?? 0),
        render: (value) => formatCurrency(value, { fractionDigits: 0 }),
      },
      {
        title: AppStrings.PAM_UTM_WITHDRAWAL_AMOUNT,
        dataIndex: "withdrawalAmount",
        key: "withdrawalAmount",
        width: 180,
        sorter: (a, b) => (a.withdrawalAmount ?? 0) - (b.withdrawalAmount ?? 0),
        render: (value) => formatCurrency(value, { fractionDigits: 0 }),
      },
      {
        title: AppStrings.PAM_UTM_NET_REVENUE,
        dataIndex: "netRevenue",
        key: "netRevenue",
        width: 160,
        sorter: (a, b) => (a.netRevenue ?? 0) - (b.netRevenue ?? 0),
        render: (value) => formatCurrency(value, { fractionDigits: 0 }),
      },
      {
        title: AppStrings.PAM_UTM_AVG_DEPOSIT,
        dataIndex: "avgDeposit",
        key: "avgDeposit",
        width: 160,
        sorter: (a, b) => (a.avgDeposit ?? 0) - (b.avgDeposit ?? 0),
        render: (value) => formatCurrency(value, { fractionDigits: 0 }),
      },
      {
        title: AppStrings.PAM_UTM_AVG_WITHDRAWAL,
        dataIndex: "avgWithdrawal",
        key: "avgWithdrawal",
        width: 160,
        sorter: (a, b) => (a.avgWithdrawal ?? 0) - (b.avgWithdrawal ?? 0),
        render: (value) => formatCurrency(value, { fractionDigits: 0 }),
      },
      {
        title: AppStrings.PAM_UTM_AVG_NET_REVENUE,
        dataIndex: "avgNetRevenue",
        key: "avgNetRevenue",
        width: 170,
        sorter: (a, b) => (a.avgNetRevenue ?? 0) - (b.avgNetRevenue ?? 0),
        render: (value) => formatCurrency(value, { fractionDigits: 0 }),
      },
    ],
    [currencySymbol],
  );

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerControls}>
            <DatePicker
              dates={dates}
              setDates={handleDatesChange}
              getData={() => {}}
              showPresets
              startRange={DATE_RANGE.CURRENT_MONTH}
            />
          </div>
        </div>

        {(isKpisError || isSourcesError) && (
          <div className={styles.errorBanner} data-testid="utm-reports-error">
            Failed to load UTM Reports. Please try again.
          </div>
        )}

        <div className={styles.filterRow}>
          <Select
            className={styles.filterSelect}
            mode="multiple"
            placeholder={AppStrings.PAM_UTM_CATEGORIZATION}
            value={draftFilters.utm_categorization}
            onChange={(value) =>
              setDraftFilters((prev) => ({
                ...prev,
                utm_categorization: value,
              }))
            }
            options={utmCategorizationOptions}
            allowClear
          />
          <Select
            className={styles.filterSelect}
            mode="multiple"
            placeholder={AppStrings.UTM_SOURCE}
            value={draftFilters.utm_source}
            onChange={(value) =>
              setDraftFilters((prev) => ({ ...prev, utm_source: value }))
            }
            options={utmSourceOptions}
            allowClear
          />
          {hasAppliedFilters && (
            <Button
              intent="neutral"
              variant="outline"
              size="large"
              onClick={handleClearFilters}
              data-testid="utm-reports-clear-filters"
            >
              {AppStrings.CLEAR_FILTERS}
            </Button>
          )}
          <Button
            intent="primary"
            size="large"
            onClick={handleApply}
            data-testid="utm-reports-apply"
          >
            {AppStrings.APPLY}
          </Button>
        </div>

        <div className={styles.kpisGrid}>
          {KPI_DEFINITIONS.map(({ key, title, maximumFractionDigits }) => {
            const card = kpiByKey.get(key);
            // Integer KPIs (Users / FTDs) keep `maximumFractionDigits: 0`,
            // which makes KpiCard omit the symbol. Currency KPIs get the
            // primary client symbol from platform config.
            const isCurrencyKpi = maximumFractionDigits > 0;
            return (
              <div key={key} className={styles.kpiCardWrapper}>
                <KpiCard
                  title={title}
                  value={card?.value ?? 0}
                  maximumFractionDigits={maximumFractionDigits}
                  backgroundColor={KPI_CARD_BACKGROUND.DARK}
                  isApiRunning={isKpisLoading}
                  hidePercentage
                  currencySymbol={isCurrencyKpi ? currencySymbol : undefined}
                />
              </div>
            );
          })}
        </div>

        <div className={styles.tableSection}>
          <DataTable
            rowKey="key"
            columns={columns}
            dataSource={tableRows}
            loading={isSourcesLoading}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalRows,
              showSizeChanger: true,
              pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
              onChange: (nextPage, nextSize) => {
                // Page-size changes reset to page 1 — paging further into
                // a slice whose size just changed would otherwise land on
                // a page that may no longer exist.
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setCurrentPage(1);
                } else {
                  setCurrentPage(nextPage);
                }
              },
            }}
          />
        </div>
      </div>
    </>
  );
};

export default UtmReports;
